import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share2 } from "lucide-react";

/**
 * PWA install banner for AnimalMela.
 * - Uses the native `beforeinstallprompt` on Chrome/Edge/Samsung Internet.
 * - Falls back to an iOS-Safari "Add to Home Screen" hint (no native prompt exists).
 * - Hidden when app is already installed (`display-mode: standalone` or iOS `navigator.standalone`).
 * - "Later" snoozes the banner for 30 days.
 */

const LATER_KEY = "am_pwa_snooze_until";
const SNOOZE_DAYS = 30;

const isStandalone = () => {
  if (typeof window === "undefined") return true;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true; // iOS
  } catch (_) { /* noop */ }
  return false;
};

const isNativeShell = () => {
  try { return !!window?.Capacitor?.isNativePlatform?.(); } catch (_) { return false; }
};

const isIos = () => {
  try {
    const ua = window.navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  } catch (_) { return false; }
};

const snoozed = () => {
  try {
    const until = Number(localStorage.getItem(LATER_KEY) || 0);
    return until && Date.now() < until;
  } catch (_) { return false; }
};

const snoozeFor30Days = () => {
  try {
    const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(LATER_KEY, String(until));
  } catch (_) { /* noop */ }
};

export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isNativeShell() || isStandalone() || snoozed()) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      try { localStorage.removeItem(LATER_KEY); } catch (_) { /* noop */ }
    };

    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari has no beforeinstallprompt — show the hint variant on mobile only.
    if (isIos()) {
      const t = setTimeout(() => setIosHint(true), 1500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBip);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome !== "accepted") snoozeFor30Days();
    } catch (e) { console.debug("PWA install prompt failed", e); }
    setDeferred(null);
    setVisible(false);
  };

  const later = () => {
    snoozeFor30Days();
    setVisible(false);
    setIosHint(false);
  };

  if (!visible && !iosHint) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-0 bottom-0 z-[70] px-3 pb-3 pointer-events-none"
    >
      <div className="mx-auto max-w-md rounded-2xl border border-emerald-200 bg-white shadow-2xl p-4 flex items-start gap-3 pointer-events-auto animate-in slide-in-from-bottom-5 fade-in duration-300">
        <img
          src="/icons/icon-192.png"
          alt="AnimalMela"
          className="shrink-0 w-11 h-11 rounded-xl object-contain bg-white ring-1 ring-emerald-100"
        />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base leading-tight">Install AnimalMela</div>
          <div className="text-xs text-muted-foreground mt-1 leading-snug">
            {iosHint && !visible
              ? (<>Tap <Share2 className="inline w-3.5 h-3.5 mx-0.5 align-[-2px]" /> then <b>Add to Home Screen</b> for a faster, full-screen experience.</>)
              : "Install AnimalMela for a faster, full-screen experience and quick access from your home screen."}
          </div>
          <div className="mt-3 flex items-center gap-2">
            {visible && (
              <Button
                data-testid="pwa-install-btn"
                onClick={install}
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-9 px-4"
              >
                <Download className="w-4 h-4 mr-1.5" /> Install
              </Button>
            )}
            <Button
              data-testid="pwa-later-btn"
              variant="ghost"
              onClick={later}
              className="rounded-full h-9 px-4 text-muted-foreground"
            >
              Later
            </Button>
          </div>
        </div>
        <button
          data-testid="pwa-close-btn"
          aria-label="Dismiss"
          onClick={later}
          className="shrink-0 -mr-1 -mt-1 p-1 rounded-full text-muted-foreground hover:bg-muted"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
